import { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { Box, Typography, InputBase, Button } from '@mui/material';
import { styles } from './set-new-password.style';
import { ROUTES } from '../../../helpers/constans';
import { setNewPasswordSchema } from '../../../helpers/formik-shemas/auth.schema';
import { fetchSetNewPassword } from '../../../services/auth.service';

interface IFormValues {
  password: string;
  confirmPassword: string;
}

const SetNewPassword = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  useEffect(() => {
    const codeParam = searchParams.get('code');
    
    if (!codeParam) {
      console.warn('No code parameter found in URL');
      navigate(ROUTES.resetPassword);
      return;
    }
    
    setCode(codeParam);
  }, [searchParams, navigate]);
  const formik = useFormik<IFormValues>({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: setNewPasswordSchema,
    validateOnChange: true,
    onSubmit: () => {},
  });

  const handleSetPassword = async (event: any): Promise<void> => {
    event.preventDefault();
    
    if (!code) {
      setMessage('Invalid reset code');
      return;
    }
    
    const validation = await formik.validateForm();
    if (Object.keys(validation).length > 0) {
      formik.setErrors(validation);
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await fetchSetNewPassword({ 
        code, 
        newPassword: formik.values.password 
      });
      setMessage(response.message);
      setIsSuccess(true);
      formik.resetForm();
      
      // Navigate to login after 2 seconds
      setTimeout(() => {
        navigate(ROUTES.login);
      }, 2000);
    } catch (error: any) {
      setIsSuccess(false);
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    navigate(ROUTES.login);
  };

  return (
    <Box sx={styles.wrapper}>
      <Box
        component="form"
        sx={styles.container}
        onSubmit={handleSetPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      >
        <Typography variant="h4" sx={styles.title}>
          Set New Password
        </Typography>
        <Typography sx={styles.instruction}>
          Enter your new password below. Password must be at least 8 characters long.
        </Typography>
        {message && (
          <Typography sx={{ mb: 2, color: isSuccess ? 'success.main' : 'error.main' }}>
            {message}
          </Typography>
        )}
        <Box sx={styles.inputBox}>
          <Typography sx={styles.inputLabel}>Password</Typography>
          <InputBase
            sx={styles.input}
            name="password"
            type="password"
            placeholder="Password"
            value={formik.values.password}
            error={Boolean(formik.values.password) && Boolean(formik.errors.password)}
          />
          <Typography sx={styles.inputErrorLabel}>{formik.errors.password}</Typography>
        </Box>
        <Box sx={styles.inputBox}>
          <Typography sx={styles.inputLabel}>Confirm Password</Typography>
          <InputBase
            sx={styles.input}
            name="confirmPassword"
            type="password"
            placeholder="Confirm Password"
            value={formik.values.confirmPassword}
            error={Boolean(formik.values.confirmPassword) && Boolean(formik.errors.confirmPassword)}
          />
          <Typography sx={styles.inputErrorLabel}>{formik.errors.confirmPassword}</Typography>
        </Box>
        <Button sx={styles.button} onClick={handleSetPassword} disabled={isLoading || isSuccess}>
          {isLoading ? 'Setting Password...' : isSuccess ? 'Success!' : 'Set Password'}
        </Button>
        <Box sx={styles.backToLoginBlock}>
          <Typography sx={styles.backToLoginTitle}>Remember your password?</Typography>
          <Box component="span" sx={styles.link} onClick={handleBackToLogin}>
            Back to Login
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SetNewPassword;